/**
 * App Layout Component
 * Main layout with sidebar navigation, header, and content area
 *
 * Responsive behavior:
 * - Mobile (< md): Sidebar hidden, hamburger menu shows overlay
 * - Tablet (md-lg): Sidebar always visible, collapsed (icons only)
 * - Desktop (>= lg): Sidebar always visible, expanded by default
 */

import { AppSidebar } from './AppSidebar';
import { AppHeader } from './AppHeader';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Set initial state based on screen size
    const width = window.innerWidth;
    // Tablet (768-1024): default collapsed
    // Desktop (>= 1024): default expanded
    return width >= 768 && width < 1024;
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <div className="flex h-screen overflow-hidden warm-gradient-bg">
      <AppSidebar
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
        onCollapseToggle={() => setIsCollapsed(!isCollapsed)}
      />

      {/* Spacer div for tablet/desktop - matches sidebar width */}
      <div
        className={cn(
          'hidden md:block flex-shrink-0 transition-all duration-300',
          isCollapsed ? 'w-16' : 'w-64'
        )}
      />

      {/* Main content area with header */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0 max-w-[100vw]">
        <AppHeader
          onMobileMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          isMobileMenuOpen={isMobileMenuOpen}
          onCollapseToggle={() => setIsCollapsed(!isCollapsed)}
          isCollapsed={isCollapsed}
        />
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
